import {
  AlertTriangle,
  ArrowLeft,
  CircleDollarSign,
  HandCoins,
  ShieldAlert,
  Sparkles,
} from "lucide-react";
import { AlertCard } from "../components/AlertCard";
import { AutomationsHealthPanel } from "../components/AutomationsHealthPanel";
import { ClientRail } from "../components/ClientRail";
import { LoadingState } from "../components/LoadingState";
import { MetricCard } from "../components/MetricCard";
import { useDashboard } from "../context/DashboardContext";
import { currency } from "../lib/format";

export function MorningCheckPage() {
  const { data, selectedClient, setSelectedClient, loading, error, refresh } = useDashboard();

  if (loading && !data) return <LoadingState />;
  if (error || !data) {
    return (
      <section className="error-state">
        <ShieldAlert size={28} />
        <h1>No pudimos abrir el Morning Check</h1>
        <p>{error ?? "El servicio no devolvió datos."}</p>
        <button className="button button-primary" type="button" onClick={() => void refresh()}>
          Reintentar
        </button>
      </section>
    );
  }

  const selected =
    selectedClient === "ALL"
      ? null
      : data.clients.find((client) => client.clientSlug === selectedClient) ?? null;
  const filteredAlerts =
    selectedClient === "ALL"
      ? data.alerts
      : data.alerts.filter((alert) => alert.clientSlug === selectedClient);
  const seoAlerts = filteredAlerts.filter((alert) => alert.domain === "SEO");
  const paidAlerts = filteredAlerts.filter((alert) => alert.domain === "MKT");
  const pipelineAlerts = filteredAlerts.filter((alert) => alert.domain === "PIPELINE");
  const inactiveClients = data.clients.filter((client) => client.budgetMonthEst <= 0);

  const metrics = selected
    ? {
        spendMtd: selected.spendMtd,
        activeClients: 1,
        over: selected.status === "OVER" ? 1 : 0,
        atRisk: selected.status === "AT_RISK" ? 1 : 0,
        criticalAlerts: selected.criticalCount,
        recoverable: filteredAlerts
          .filter((alert) => alert.domain === "MKT" && (alert.impact ?? "").trim().startsWith("$"))
          .reduce((sum, alert) => {
          const numeric = Number((alert.impact ?? "").replace(/[^\d]/g, ""));
          return sum + (Number.isFinite(numeric) ? numeric : 0);
        }, 0),
      }
    : data.portfolio;

  const headline = selected
    ? `${selected.displayName}: ${selected.alertCount || "sin"} alertas activas · ritmo ${Math.round(
        selected.pacingPct * 100,
      )}% · ${currency.format(selected.spendMtd)} invertidos MTD.`
    : data.headline;

  return (
    <div className="page page-morning">
      <section className="page-intro enter">
        <div>
          {selected && (
            <button
              className="segment-back"
              type="button"
              onClick={() => setSelectedClient("ALL")}
              aria-label="Volver a la vista completa de la cartera"
            >
              <ArrowLeft size={15} aria-hidden="true" />
              Toda la cartera
            </button>
          )}
          <span className="eyebrow">Control operativo · {data.dataAsOf}</span>
          <h1>{selected ? selected.displayName : "Morning Check"}</h1>
          <p>
            {selected
              ? "Vista segmentada: todos los indicadores y alertas responden al cliente activo."
              : "Lo importante de la cartera, ordenado para decidir antes de entrar al detalle."}
          </p>
        </div>
        <div className="live-badge">
          <span />
          Datos actualizados
        </div>
      </section>

      <section className="briefing-bar enter" style={{ animationDelay: "40ms" }}>
        <span className="briefing-icon">
          <Sparkles size={17} />
        </span>
        <p>{headline}</p>
      </section>

      <section className="metric-grid">
        <MetricCard
          label="Inversión"
          value={currency.format(metrics.spendMtd)}
          note="MTD"
          icon={CircleDollarSign}
          tone="neutral"
          delay={70}
        />
        <MetricCard
          label="Over / en riesgo"
          value={`${metrics.over} / ${metrics.atRisk}`}
          note={`${metrics.activeClients} activos`}
          icon={AlertTriangle}
          tone={metrics.over > 0 ? "danger" : metrics.atRisk > 0 ? "warning" : "good"}
          delay={105}
        />
        <MetricCard
          label="Ahorro recuperable"
          value={currency.format(metrics.recoverable)}
          note={selected ? "estimado" : `${data.portfolio.recoverableClients} clientes`}
          icon={HandCoins}
          tone={metrics.recoverable > 0 ? "accent" : "good"}
          delay={140}
        />
        <MetricCard
          label="Alertas críticas"
          value={String(metrics.criticalAlerts)}
          note="hoy"
          icon={ShieldAlert}
          tone={metrics.criticalAlerts > 0 ? "danger" : "good"}
          delay={175}
        />
      </section>

      <section className="operations-grid">
        <ClientRail
          clients={
            selected
              ? [selected]
              : data.clients.filter((client) => client.budgetMonthEst > 0)
          }
        />
        <section className="alert-workspace panel">
          <div className="panel-heading">
            <div>
              <span className="eyebrow orange">Prioridad de hoy</span>
              <h2>Alertas accionables</h2>
            </div>
            <span className="count-badge">{filteredAlerts.length}</span>
          </div>
          <div className="alert-columns">
            <section className="alert-column">
              <div className="alert-column-heading seo">
                <span>SEO</span>
                <small>{seoAlerts.length} alertas</small>
              </div>
              {seoAlerts.length ? (
                seoAlerts.map((alert, index) => (
                  <AlertCard key={alert.id} alert={alert} index={index} />
                ))
              ) : (
                <div className="empty-column">
                  <span>✓</span>
                  <p>Sin alertas SEO para este segmento.</p>
                </div>
              )}
            </section>
            <section className="alert-column">
              <div className="alert-column-heading paid">
                <span>Paid Media</span>
                <small>{paidAlerts.length} alertas</small>
              </div>
              {paidAlerts.length ? (
                paidAlerts.map((alert, index) => (
                  <AlertCard key={alert.id} alert={alert} index={index + seoAlerts.length} />
                ))
              ) : (
                <div className="empty-column">
                  <span>✓</span>
                  <p>Sin alertas Paid para este segmento.</p>
                </div>
              )}
            </section>
          </div>
        </section>
      </section>
      <section className="morning-secondary-grid">
        <details className="panel disclosure-panel">
          <summary>Clientes sin inversión activa <span>{inactiveClients.length}</span></summary>
          <div className="inactive-client-list">
            {inactiveClients.map((client) => <button type="button" key={client.clientSlug} onClick={() => setSelectedClient(client.clientSlug)}><strong>{client.displayName}</strong><small>{client.alertCount} alertas</small></button>)}
            {!inactiveClients.length && <p>Todos los clientes tienen inversión activa.</p>}
          </div>
        </details>
        <details className="panel disclosure-panel" open={pipelineAlerts.some((alert) => alert.severity === "CRITICA")}>
          <summary>Estado del pipeline de datos <span>{pipelineAlerts.length} alertas</span></summary>
          <div className="stack-list">
            {pipelineAlerts.map((alert, index) => <AlertCard key={alert.id} alert={alert} index={index} />)}
            {!pipelineAlerts.length && <div className="empty-column"><span>✓</span><p>Pipeline sin alertas activas.</p></div>}
          </div>
        </details>
        <AutomationsHealthPanel />
      </section>
    </div>
  );
}
